// ============================================================
// DockerMultiStage.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: "Build in one, Run in another."
// - Key Term: "Artifact" (The compiled/ready-to-run file).
// - Strategy: Visualize the reduction from 800MB to 50MB.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import EngineeringIcon from "@mui/icons-material/Engineering";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SpeedIcon from "@mui/icons-material/Speed";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#0DB7ED";
const NAVY_BG = "#001E2B";
const TERMINAL_BG = "#011627";
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.6)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(13, 183, 237, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const StageCard = styled(Paper)(({ theme, type }) => ({
    padding: theme.spacing(4),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `2px solid ${type === 'build' ? DOCKER_BLUE : '#00ED64'}`,
    borderRadius: "16px",
    textAlign: "center",
    height: "100%",
    position: 'relative'
}));

const DockerfileWindow = styled(Paper)(({ theme }) => ({
    backgroundColor: TERMINAL_BG,
    padding: theme.spacing(3),
    borderRadius: "8px",
    border: `1px solid ${BRAND.border}`,
    fontFamily: "'Fira Code', monospace",
    fontSize: '0.85rem'
}));

export default function DockerMultiStage() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Optimization · Module 11"
                            sx={{ bgcolor: "rgba(13, 183, 237, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Multi-Stage <span style={{ color: DOCKER_BLUE }}>Builds</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        Why ship the kitchen when you only need the cake? Multi-stage builds
                        keep your production images tiny, secure, and lightning-fast.
                    </Typography>
                </Box>

                {/* 2. THE TRANSFORMATION VISUAL */}

                <Grid container spacing={4} sx={{ mb: 10 }} alignItems="stretch">
                    <Grid item xs={12} md={5}>
                        <StageCard type="build">
                            <EngineeringIcon sx={{ fontSize: 50, color: DOCKER_BLUE, mb: 2 }} />
                            <Typography variant="h6" fontWeight={800}>Stage 1: The Builder</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mt: 2 }}>
                                Contains compilers, dev-dependencies, and source code.
                                <strong> Result: ~850MB</strong>
                            </Typography>
                        </StageCard>
                    </Grid>

                    <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <FilterAltIcon sx={{ fontSize: 40, color: DOCKER_BLUE, opacity: 0.5 }} />
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <StageCard type="run">
                            <VerifiedUserIcon sx={{ fontSize: 50, color: "#00ED64", mb: 2 }} />
                            <Typography variant="h6" fontWeight={800}>Stage 2: Production</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mt: 2 }}>
                                Contains ONLY the compiled app and runtime.
                                <strong> Result: ~45MB</strong>
                            </Typography>
                        </StageCard>
                    </Grid>
                </Grid>

                {/* 3. THE CODE PATTERN */}
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 800 }}>
                    The <span style={{ color: DOCKER_BLUE }}>"AS"</span> Keyword
                </Typography>
                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={7}>
                        <DockerfileWindow>
                            <Typography sx={{ color: "rgba(255,255,255,0.3)", mb: 1 }}># Stage 1: Build</Typography>
                            <Typography sx={{ color: "#C792EA", mb: 0.5 }}>FROM <span style={{ color: "#82AAFF" }}>node:18</span> AS <span style={{ color: DOCKER_BLUE }}>build-env</span></Typography>
                            <Typography sx={{ color: "#82AAFF", ml: 2, mb: 0.5 }}>WORKDIR /app</Typography>
                            <Typography sx={{ color: "#82AAFF", ml: 2, mb: 1 }}>RUN npm install && npm run build</Typography>

                            <Typography sx={{ color: "rgba(255,255,255,0.3)", mb: 1, mt: 2 }}># Stage 2: Production</Typography>
                            <Typography sx={{ color: "#C792EA", mb: 0.5 }}>FROM <span style={{ color: "#82AAFF" }}>nginx:alpine</span></Typography>
                            <Typography sx={{ color: "#C792EA", ml: 2, mb: 0.5 }}>
                                COPY <span style={{ color: DOCKER_BLUE }}>--from=build-env</span> /app/dist /usr/share/nginx/html
                            </Typography>
                        </DockerfileWindow>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>The Magic Move</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.7 }}>
                                    The <code>--from</code> flag allows you to reach into a previous stage and pull out specific files.
                                    Everything else in the first stage is discarded.
                                </Typography>
                            </Box>
                            <Divider sx={{ bgcolor: BRAND.border }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <SpeedIcon sx={{ color: "#00ED64" }} />
                                <Typography variant="body2">Faster deployments and pulling.</Typography>
                            </Box>
                        </Stack>
                    </Grid>
                </Grid>

                {/* 4. KEY BENEFITS */}
                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Smaller Footprint",
                            icon: <FitnessCenterIcon />,
                            desc: "Removing build tools reduces disk space and network bandwidth significantly."
                        },
                        {
                            title: "Enhanced Security",
                            icon: <VerifiedUserIcon />,
                            desc: "Fewer packages in production means a smaller 'attack surface' for hackers."
                        },
                        {
                            title: "Simplified CI/CD",
                            icon: <SpeedIcon />,
                            desc: "You don't need a separate build server; Docker handles the build environment internally."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Paper sx={{ p: 4, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4, textAlign: 'center' }}>
                                <Box sx={{ color: DOCKER_BLUE, mb: 2 }}>{item.icon}</Box>
                                <Typography variant="h6" fontWeight={800} gutterBottom>{item.title}</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted }}>{item.desc}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Optimized? Let's explore Docker Hub.</Typography>
                    <Link to="/docker-hub" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(13, 183, 237, 0.3)`,
                                "&:hover": { bgcolor: "#0091ca" }
                            }}
                        >
                            Next: Docker Hub Mastery
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Authorized Training by {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
